import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { MPMLayout } from 'components/layout'
import { BackButton } from 'components/button/back-button'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useInsightsData } from 'hooks/insights/useInsightsData'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useDwellTime } from 'utils/analytics'
import type { Insight } from 'types/insights'
import { InsightsTab } from '../utils'
import { determineSummaryDisplayDate } from '../summary/utils'
import { InsightDetailsHeader } from './insight-details-header'
import { WhereItsHappening } from './where-its-happening'
import { RecommendedNextMove } from './recommended-next-move'
import './insight-details.scss'
import { Grid } from '@design-systems/canvas-ui'
import { UrlParams } from 'enums/url-params'

export const InsightDetailsPage = () => {
    const { t } = useEssentials()
    const { insights: insightsKeys } = TRANSLATION_KEYS
    const { details } = insightsKeys
    const location = useLocation()
    const { data, renderGuard } = useInsightsData()
    useDwellTime('insights/details')

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const params = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    )
    const tab = params.get('tab')
    const source = params.get('source')
    const index = Number(params.get('index') ?? 0)
    const yesterdayTab = tab !== InsightsTab.Weekly
    const isSummary = source === 'summary'
    const backTab = tab ?? InsightsTab.Yesterday
    const backPath = `/insights/${backTab}`

    const heading = t(insightsKeys.title)

    if (renderGuard) {
        return (
            <MPMLayout
                heading={heading}
                isMenuOverride
                menuOverride={
                    <BackButton
                        fallbackPath={backPath}
                        preserveQueryParams={[UrlParams.LOCATION_ID]}
                    />
                }
                breadcrumbsEnabled={false}
            >
                {renderGuard}
            </MPMLayout>
        )
    }

    const tabData = yesterdayTab ? data?.daily_insights : data?.weekly_insights

    const insightDetails = isSummary
        ? tabData?.summary?.summary_details
        : (tabData?.insights as Insight[] | undefined)?.[index]?.insight_details

    const startDate = yesterdayTab
        ? data?.daily_insights?.date
        : data?.weekly_insights?.week_starting_date
    const endDate = yesterdayTab
        ? undefined
        : data?.weekly_insights?.week_ending_date

    const dateLabel = determineSummaryDisplayDate(
        yesterdayTab,
        startDate,
        endDate,
    )
    const datePrefix = t(yesterdayTab ? details.yesterday : details.week)

    const title = isSummary
        ? (insightDetails?.title ?? '')
        : ((tabData?.insights as Insight[] | undefined)?.[index]?.title ??
          insightDetails?.title ??
          '')

    const iconKey = isSummary
        ? tabData?.summary?.insight_icon
        : (tabData?.insights as Insight[] | undefined)?.[index]?.insight_icon

    const recommendedActions = yesterdayTab
        ? []
        : (insightDetails?.recommended_actions ?? [])

    return (
        <MPMLayout
            heading={heading}
            isMenuOverride
            menuOverride={
                <BackButton
                    fallbackPath={backPath}
                    preserveQueryParams={[UrlParams.LOCATION_ID]}
                />
            }
            breadcrumbsEnabled={false}
            className="hc8-ph-none hc8-pb-none hc8-bg-base"
        >
            <Grid.Container
                direction="column"
                className={`hc8-wth-100 hc8-ph-4x hc8-pv-4x`}
                spacing="expanded"
                noWrap
            >
                <Grid.Item>
                    <InsightDetailsHeader
                        title={title}
                        iconKey={iconKey}
                        datePrefix={datePrefix}
                        dateLabel={dateLabel}
                        whatChanged={insightDetails?.what_changed ?? []}
                        whyItMatters={insightDetails?.why_it_matters ?? ''}
                    />
                </Grid.Item>
                <Grid.Item>
                    <WhereItsHappening
                        observations={insightDetails?.where_its_happening}
                    />
                </Grid.Item>
            </Grid.Container>
            <RecommendedNextMove actions={recommendedActions} />
        </MPMLayout>
    )
}
